import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { useDropZone } from './dropzone-provider';

interface DropZoneSelectedFilesProps {
  /**
   * Additional CSS classes to apply to the selected files container.
   */
  className?: string;
  /**
   * Optional children render prop for custom file rendering.
   * If not provided, will use the default SelectedFile component.
   *
   * @example
   * <DropZone.SelectedFiles>
   *   {(props) => <div>Custom file rendering</div>}
   * </DropZone.SelectedFiles>
   */
  children?:
    | React.ReactNode
    | ((props: {
        file: File;
        onRemove: (file: File) => void;
        className?: string;
      }) => React.ReactNode);
}

const classes = cva({
  base: 'grid grid-cols-1 gap-6',
  variants: {
    keepContainerHeight: {
      // Need to keep the container height so the layout does not grow or shrink.
      // This is due to the extra height for DropZoneError.
      true: 'min-h-[180px]',
      false: 'max-h-[250px] overflow-y-auto',
    },
  },
});

function DropZoneSelectedFiles(props: DropZoneSelectedFilesProps) {
  const { className, children } = props;
  const { droppedFiles, allowsMultiple, removeFile } = useDropZone();

  // If the children prop is a function, use it to render the component.
  const isFunction = typeof children === 'function';

  return (
    <div className={cx(classes({ keepContainerHeight: !allowsMultiple && droppedFiles.length > 0 }), className)}>
      {isFunction
        ? droppedFiles.map((file) => {
            const fileProps = {
              file,
              onRemove: removeFile,
              // If the dropzone is not allowing multiple files,
              // set the min-height to 134px so the layout does not grow or shrink.
              className: !allowsMultiple ? 'h-67' : undefined,
            };
            return <React.Fragment key={file.name}>{children(fileProps)}</React.Fragment>;
          })
        : children}
    </div>
  );
}

export { DropZoneSelectedFiles };
export type { DropZoneSelectedFilesProps };
